/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Dimension } from '@opengeoweb/webmap';
import type { AnySentryStateMap, SentryEntities, SentryState } from './types';

export const getUrlHost = (url?: string): string | undefined => {
  if (!url) {
    return undefined;
  }
  try {
    return new URL(url, window.location.href).host || undefined;
  } catch {
    return undefined;
  }
};

export const pickList = <T, R>(
  list: T[] | undefined,
  pickItem: (item: T) => R,
): R[] | undefined => list?.map(pickItem);

export const pickRecord = <T, R>(
  record: Partial<Record<string, T>> | undefined,
  pickValue: (value: T, key: string) => R,
): Record<string, R> | undefined =>
  record &&
  Object.fromEntries(
    Object.entries(record)
      .filter((entry): entry is [string, T] => entry[1] !== undefined)
      .map(([key, value]) => [key, pickValue(value, key)]),
  );

export const pickEntities = <T>(
  entities: SentryEntities<T> | undefined,
  pickEntity: (entity: T, id: string) => SentryState<T>,
): SentryState<SentryEntities<T>> | undefined =>
  entities &&
  ({
    allIds: entities.allIds,
    byId: Object.fromEntries(
      entities.allIds
        .filter((id) => entities.byId[id] !== undefined)
        .map((id) => [id, pickEntity(entities.byId[id], id)]),
    ),
  } as SentryState<SentryEntities<T>>);

export const pickDimension = (
  dimension: Dimension,
): SentryState<Dimension> => ({
  name: dimension.name,
  units: dimension.units,
  currentValue: dimension.currentValue,
  synced: dimension.synced,
});

const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object';

export const unifySentryStateMaps = (
  sentryStateMaps: AnySentryStateMap[],
): AnySentryStateMap => {
  const unified: AnySentryStateMap = {};
  for (const sentryStateMap of sentryStateMaps) {
    for (const [key, picker] of Object.entries(sentryStateMap)) {
      if (key in unified && unified[key] !== picker) {
        throw new Error(
          `Sentry state picker ${key} is already defined with a different picker`,
        );
      }
      unified[key] = picker;
    }
  }
  return unified;
};

export const createSentryStatePicker =
  (sentryStateMap: AnySentryStateMap) =>
  (state: unknown): object | undefined => {
    if (!isObject(state)) {
      return undefined;
    }
    return Object.fromEntries(
      Object.entries(sentryStateMap).flatMap(([key, picker]) => {
        const slice = state[key];
        if (!picker || slice === undefined) {
          return [];
        }
        try {
          return [[key, picker(slice as never)]];
        } catch {
          return [];
        }
      }),
    );
  };
